/*
 * touchGeometry — Pure Geometrie-Helper fuer Touch-/Maus-Hit-Tests
 *
 * Aus mouseHandlers.js herausgeloest (System-Review V6, 2026-06-12): dort
 * lagen alle 4 Touch-Bugs der letzten Wochen, aber 0 Tests — diese Kerne
 * sind pur (keine ctx-/Layout-/Renderer-Abhaengigkeit) und damit direkt
 * testbar. mouseHandlers.js bleibt der einzige Aufrufer und liefert die
 * Bounds/Slops/Probes aus dem Live-Kontext zu.
 */

/**
 * Abstand zweier Touch-Punkte (fuer Two-Finger-Pinch).
 * @param {{clientX:number, clientY:number}} t1
 * @param {{clientX:number, clientY:number}} t2
 * @returns {number} Distanz in Pixeln
 */
export function twoFingerDistance(t1, t2) {
    const dx = t1.clientX - t2.clientX
    const dy = t1.clientY - t2.clientY
    return Math.sqrt(dx * dx + dy * dy)
}

/**
 * Strikte Bounds-Pruefung: liegt (x, y) im Rechteck?
 * Leere/degenerierte Bounds (width/height <= 0) treffen nie.
 * @param {{x:number, y:number, width:number, height:number}|null} bounds
 * @param {number} x
 * @param {number} y
 * @returns {boolean}
 */
export function isWithinBounds(bounds, x, y) {
    if (!bounds || bounds.width <= 0 || bounds.height <= 0) return false
    return x >= bounds.x && x < bounds.x + bounds.width
        && y >= bounds.y && y < bounds.y + bounds.height
}

/**
 * Bounds-Pruefung mit Slop-Puffer: das Rechteck waechst um dx/dy in jede
 * Richtung (Touch-Toleranz am Rand).
 * @param {{x:number, y:number, width:number, height:number}|null} bounds
 * @param {number} x
 * @param {number} y
 * @param {{dx:number, dy:number}} slop
 * @returns {boolean}
 */
export function isWithinBoundsWithSlop(bounds, x, y, slop) {
    if (!bounds) return false
    if (x < bounds.x - slop.dx || x >= bounds.x + bounds.width + slop.dx) return false
    if (y < bounds.y - slop.dy || y >= bounds.y + bounds.height + slop.dy) return false
    return true
}

/**
 * Slop-Spirale: probiert erst die exakte Position, dann die Box
 * [-dx..+dx]x[-dy..+dy] und liefert den Treffer mit der kleinsten
 * euklidischen Distanz. Die Probe-Funktion kapselt den eigentlichen
 * Hit-Test (z.B. getSeparatorControlAtPosition).
 *
 * @param {number} lx - Lokale X-Position (exakter Tap-Punkt)
 * @param {number} ly - Lokale Y-Position
 * @param {{dx:number, dy:number}} slop
 * @param {(x:number, y:number) => Object|null} probe
 * @returns {Object|null} Naehester Treffer oder null
 */
export function findNearestHitWithSlop(lx, ly, slop, probe) {
    // Pixelgenau zuerst — wenn schon Treffer, fertig
    const exact = probe(lx, ly)
    if (exact) return exact

    let best = null
    let bestDistSq = Infinity
    for (let dy = -slop.dy; dy <= slop.dy; dy++) {
        for (let dx = -slop.dx; dx <= slop.dx; dx++) {
            if (dx === 0 && dy === 0) continue
            const hit = probe(lx + dx, ly + dy)
            if (!hit) continue
            const distSq = dx * dx + dy * dy
            if (distSq < bestDistSq) {
                bestDistSq = distSq
                best = hit
            }
        }
    }
    return best
}

/**
 * Slop-Hit-Test fuer einen Selection-Endpunkt (Anchor oder Cursor).
 *
 * X-Vergleich: bei Proportional in Pixeln gegen `pos.pixelX` (echte
 * Pixel-Position via posMap), sonst in Char-Spalten gegen `pos.x` —
 * `dataToScreen` liefert fuer Proportional nur eine Char-Spalten-
 * Approximation, die mit der Zeilenlaenge nach links drifted (Lesson
 * Touch-Phase 5). dxNorm ist auf Char-Einheiten normiert, damit der
 * distSq-Tiebreaker zwischen Anchor und Cursor konsistent vergleicht.
 *
 * @param {Object} params
 * @param {number} params.lx - Pane-lokale Char-X des Taps
 * @param {number} params.ly - Pane-lokale Char-Y des Taps
 * @param {number|null} params.localPixelX - Pane-lokale Pixel-X (null wenn unbekannt)
 * @param {{x:number, y:number, pixelX?:number}} params.pos - Endpunkt-Screen-Position
 * @param {{dx:number, dy:number}} params.slop
 * @param {boolean} params.isProportional
 * @param {number} params.charWidth - Pixel pro Char-Spalte
 * @returns {{distSq:number}|null} Trefferdistanz² (Char-Einheiten) oder null
 */
export function hitTestSelectionEndpoint({ lx, ly, localPixelX, pos, slop, isProportional, charWidth }) {
    let dxNorm
    if (isProportional && typeof pos.pixelX === 'number' && localPixelX !== null) {
        const dxPx = localPixelX - pos.pixelX
        if (Math.abs(dxPx) > slop.dx * charWidth) return null
        dxNorm = dxPx / charWidth
    } else {
        const dx = lx - pos.x
        if (Math.abs(dx) > slop.dx) return null
        dxNorm = dx
    }
    const dy = ly - pos.y
    if (Math.abs(dy) > slop.dy) return null
    return { distSq: dxNorm * dxNorm + dy * dy }
}
